/**
 * F 331 CORRECTIVE — THE TELEMETRY SINK.
 *
 * WHAT THE CORRECTIVE ASSUMED, AND WHAT WAS ACTUALLY THERE
 * --------------------------------------------------------
 * The corrective said to "use the telemetry mechanism from F 322: the probe
 * writes the RPM and RPD state to a live-only telemetry stream, the UI renders
 * these using the telemetry widget face".
 *
 * **F 322 built the retention POLICY, not the stream.** `telemetrySetting.ts`
 * resolves *how long* a signal should be kept — `resolveRetention()` returns a
 * `{signal, mode, window, statedCost}` value and re-derives it so the cost can
 * never be forged. There was no sink, no writer, no collector and no widget
 * anywhere in `src/`. (`Span` exists and F 321 measured it as telemetry-shaped
 * with no store and no caller.)
 *
 * So the honest reading of the corrective is: the SETTINGS face exists and must
 * be used; the TELEMETRY face does not exist and must be BUILT as a primitive —
 * not as an AI-specific chart. That is this file, plus one widget face. Anything
 * in the product that wants a live reading plus a rolled-up history now has one.
 *
 * THE POLICY IS F 322's, AND IT IS NOT NEGOTIABLE HERE
 * ---------------------------------------------------
 * `live-only`  — one sample in memory. No history is retained at all. This is the
 *                default because it is the only mode with no stated cost.
 * `rolled-up`  — samples are bucketed by `window` and the buckets are kept.
 * `kept`       — every sample is retained.
 *
 * The sink calls `resolveRetention()` rather than re-deciding the policy, so a
 * user who chose `live-only` in settings gets `live-only` behaviour here without
 * this module knowing anything about the setting.
 */

import type { SettingStore } from '../settings/SettingStore';
import { resolveRetention, type RetentionMode } from './telemetrySetting';

export interface TelemetrySample {
  signal: string;
  at: number;
  value: number;
  /** Carried through to the face so it can label a reading. */
  unit?: string;
  /** Exact vs estimated, from F 330's tokenizer. Never silently upgraded. */
  countType?: 'exact-reported' | 'exact-api' | 'estimated-local' | 'unknown';
}

export interface RollupBucket {
  /** Start of the bucket, epoch ms. */
  from: number;
  to: number;
  count: number;
  min: number;
  max: number;
  sum: number;
  mean: number;
  /** True when ANY contributing sample was an estimate. */
  estimated: boolean;
}

export interface SignalState {
  signal: string;
  mode: RetentionMode;
  window: string | null;
  /** The most recent sample. Always present — this is the "live" face. */
  live: TelemetrySample | null;
  /** Retained history, empty in live-only. */
  history: RollupBucket[];
}

const WINDOW_MS: Record<string, number> = {
  '1m': 60_000,
  '5m': 300_000,
  '1h': 3_600_000,
  '6h': 21_600_000,
  '1d': 86_400_000,
  '1w': 604_800_000,
};

export function windowMs(window: string | null): number {
  return (window && WINDOW_MS[window]) || WINDOW_MS['1m'];
}

export interface TelemetrySinkLike {
  /** Write a sample. Retention is resolved per signal, per write. */
  push: (s: TelemetrySample) => void;
  /** Current state for a signal — what a face renders. */
  read: (signal: string) => SignalState;
  /** Every signal that has been written. Drives the dashboard's own list. */
  signals: () => string[];
  reset: () => void;
}

const MAX_BUCKETS = 168; // a week of 1m buckets

/**
 * Create a sink bound to a `SettingStore`, so retention is the user's choice.
 *
 * `retentionFor` is injectable because the tests need to exercise every mode
 * without constructing a full graph — and because a sink that could not be told
 * what the policy is would be a sink that quietly ignored the setting.
 */
export function createTelemetrySink(
  retentionFor: (signal: string) => { mode: RetentionMode; window: string | null },
  initial?: SignalState,
): TelemetrySinkLike {
  const state = new Map<string, SignalState>(initial ? Object.entries(initial) : []);
  const raw = new Map<string, TelemetrySample[]>();

  const bucketFor = (signal: string): SignalState => {
    let s = state.get(signal);
    if (!s) {
      s = { signal, mode: 'live-only', window: null, live: null, history: [] };
      state.set(signal, s);
    }
    return s;
  };

  return {
    push(sample) {
      const retention = retentionFor(sample.signal);
      const s = bucketFor(sample.signal);
      s.mode = retention.mode;
      s.window = retention.window;
      s.live = sample;

      if (retention.mode === 'live-only') {
        // No history. Not "history that we will prune later" — none.
        return;
      }

      const list = raw.get(sample.signal) ?? [];
      list.push(sample);
      raw.set(sample.signal, list);

      if (retention.mode === 'kept') {
        // Kept means every SAMPLE, projected onto singleton buckets so one face
        // can render both shapes.
        s.history = list.slice(-MAX_BUCKETS).map((x) => ({
          from: x.at, to: x.at, count: 1, min: x.value, max: x.value, sum: x.value,
          mean: x.value,
          estimated: x.countType === 'estimated-local' || x.countType === 'unknown',
        }));
        return;
      }

      // rolled-up
      const size = windowMs(retention.window);
      const buckets = new Map<number, RollupBucket>();
      for (const x of list) {
        const from = Math.floor(x.at / size) * size;
        const cur = buckets.get(from) ?? {
          from, to: from + size, count: 0, min: Infinity, max: -Infinity, sum: 0, mean: 0, estimated: false,
        };
        cur.count += 1;
        cur.min = Math.min(cur.min, x.value);
        cur.max = Math.max(cur.max, x.value);
        cur.sum += x.value;
        cur.estimated = cur.estimated || x.countType === 'estimated-local' || x.countType === 'unknown';
        buckets.set(from, cur);
      }
      s.history = [...buckets.values()]
        .sort((a, b) => a.from - b.from)
        .slice(-MAX_BUCKETS)
        .map((b) => ({ ...b, mean: b.sum / b.count }));
    },

    read: (signal) => bucketFor(signal),

    signals: () => [...state.keys()].sort(),

    reset() {
      state.clear();
      raw.clear();
    },
  };
}

/** The production wiring: retention comes from the graph, via F 322's resolver. */
export function createGraphTelemetrySink(store: SettingStore): TelemetrySinkLike {
  return createTelemetrySink((signal) => {
    const r = resolveRetention(store, signal);
    return { mode: r.mode, window: r.window };
  });
}

/**
 * The signal names F 331's probe writes.
 *
 * These are NAMED, not hardcoded values — a signal name is a channel, and the
 * value arrives from the graph. The naming convention keeps the AI engine's
 * telemetry separable from any other consumer's.
 */
export const PIPELINE_SIGNALS = {
  rpmRemaining: (p: string, m: string) => `pipeline.rpm.${p}.${m}`,
  rpdRemaining: (p: string, m: string) => `pipeline.rpd.${p}.${m}`,
  tokens: (p: string, m: string) => `pipeline.tokens.${p}.${m}`,
  latency: (p: string, m: string) => `pipeline.latency.${p}.${m}`,
} as const;